import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ErrorBanner, RequiredMark, useConfirm, useModalA11y, useToast } from "./ui-kit";

type Column = { category: "backlog" | "todo" | "in_progress" | "done"; id: string; isReview?: number; name: string; position: number; wipLimit: number | null };
const api = async (path: string, init: RequestInit) => {
  const response = await fetch(path, { ...init, headers: init.body ? { "content-type": "application/json" } : undefined });
  const payload = await response.json() as { message?: string };
  if (!response.ok) throw new Error(payload.message || "Could not update the board.");
};

// One row in the reorderable column list -- the whole row still submits its
// own name/WIP/review form as before; only the grip button on the left
// participates in the drag (via dnd-kit's listeners), so clicking into the
// name/WIP inputs never gets mistaken for a drag gesture.
function SortableColumnRow({ busy, column, onRemove, onSave }: {
  busy: string; column: Column; onRemove: (column: Column) => void; onSave: (event: FormEvent<HTMLFormElement>, columnId: string) => void;
}) {
  const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({ id: column.id });
  const style: CSSProperties = { opacity: isDragging ? 0.6 : 1, transform: CSS.Transform.toString(transform), transition };
  return (
    <form onSubmit={(event) => onSave(event, column.id)} ref={setNodeRef} style={style}>
      {/* attributes/listeners/setActivatorNodeRef all live on the handle (not the
          form root) -- keyboard activation only fires when the keydown target
          matches the activator node, and putting `role="button"` on the whole
          form would be a confusing screen-reader announcement anyway. */}
      <button aria-label={`Reorder ${column.name}`} className="column-drag-handle" ref={setActivatorNodeRef} type="button" {...attributes} {...listeners}>
        <i aria-hidden="true" className="bi bi-grip-vertical" />
      </button>
      <label><span>Column name<RequiredMark /></span><input aria-label={`Column name for ${column.name}`} defaultValue={column.name} name="name" required /></label>
      <label>Category<input disabled value={column.category.replace("_", " ")} /></label>
      <label>WIP limit<input aria-label={`WIP limit for ${column.name}`} defaultValue={column.wipLimit ?? ""} min="1" name="wipLimit" placeholder="None" type="number" /></label>
      <label className="checkbox-field"><input defaultChecked={Boolean(column.isReview)} name="isReview" type="checkbox" /> This column represents code review — moving a task here will ask for a PR/document link and reviewers</label>
      <button className="button secondary" disabled={busy === column.id} type="submit">Save</button>
      <button className="text-button danger" disabled={busy === column.id} onClick={() => onRemove(column)} type="button">Delete</button>
    </form>
  );
}

export function BoardSettings({ columns, onClose, onChanged, teamId }: {
  columns: Column[]; onClose: () => void; onChanged: () => Promise<void>; teamId: string;
}) {
  const confirm = useConfirm();
  const showToast = useToast();
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // Local column order, kept in sync with the latest `columns` prop (e.g. after a
  // create/delete/reorder round-trips through onChanged) but reordered optimistically
  // on drag so the list doesn't wait a round-trip to reflect the drop.
  const [order, setOrder] = useState(() => columns.map((column) => column.id));
  useEffect(() => setOrder(columns.map((column) => column.id)), [columns]);
  const orderedColumns = order
    .map((id) => columns.find((column) => column.id === id))
    .filter((column): column is Column => Boolean(column));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const save = async (event: FormEvent<HTMLFormElement>, columnId: string) => {
    event.preventDefault(); setBusy(columnId); setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api(`/api/board-columns/${columnId}`, { body: JSON.stringify({ isReview: form.get("isReview") === "on", name: form.get("name"), wipLimit: form.get("wipLimit") || null }), method: "PATCH" });
      await onChanged();
      showToast("Column saved.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save column."); }
    finally { setBusy(""); }
  };
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy("create"); setError("");
    const form = event.currentTarget; const values = new FormData(form);
    try {
      await api("/api/board-columns", { body: JSON.stringify({ category: values.get("category"), isReview: values.get("isReview") === "on", name: values.get("name"), teamId, wipLimit: values.get("wipLimit") || null }), method: "POST" });
      form.reset(); await onChanged();
      showToast(`Column "${String(values.get("name"))}" added.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not create column."); }
    finally { setBusy(""); }
  };
  const remove = async (column: Column) => {
    const ok = await confirm({ title: `Delete the empty column "${column.name}"?`, tone: "danger" });
    if (!ok) return;
    setBusy(column.id); setError("");
    try { await api(`/api/board-columns/${column.id}`, { method: "DELETE" }); await onChanged(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Could not delete column."); }
    finally { setBusy(""); }
  };
  const reorder = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const previous = order;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next); setError("");
    try {
      await api("/api/board-columns/reorder", { body: JSON.stringify({ orderedIds: next, teamId }), method: "PUT" });
      await onChanged();
      showToast("Column order updated.");
    } catch (reason) {
      setOrder(previous);
      setError(reason instanceof Error ? reason.message : "Could not reorder columns.");
    }
  };
  return <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
    <section className="dialog board-settings-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
      <button className="dialog-close" onClick={onClose} type="button">×</button>
      <p className="eyebrow">Workflow</p><h2>Configure board</h2>
      <p>Rename columns, set an optional WIP limit — the most work any one assignee may have in that column — to keep work in progress focused, mark a column as code review to prompt for a PR/document link and reviewers whenever a task moves into it, and drag the grip handle to reorder columns on the board.</p>
      <ErrorBanner>{error}</ErrorBanner>
      <DndContext collisionDetection={closestCenter} onDragEnd={(event) => void reorder(event)} sensors={sensors}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="board-column-settings">
            {orderedColumns.map((column) => <SortableColumnRow busy={busy} column={column} key={column.id} onRemove={(target) => void remove(target)} onSave={save} />)}
          </div>
        </SortableContext>
      </DndContext>
      <form className="board-column-create" onSubmit={create}>
        <label><span>New column<RequiredMark /></span><input name="name" placeholder="Review" required /></label>
        <label>Category<select name="category" defaultValue="in_progress"><option value="backlog">Backlog</option><option value="todo">To do</option><option value="in_progress">In progress</option><option value="done">Done</option></select></label>
        <label>WIP limit<input min="1" name="wipLimit" placeholder="None" type="number" /></label>
        <label className="checkbox-field"><input name="isReview" type="checkbox" /> This column represents code review — moving a task here will ask for a PR/document link and reviewers</label>
        <button className="button primary" disabled={busy === "create"} type="submit">Add column</button>
      </form>
      <div className="dialog-actions"><button className="button secondary" onClick={onClose} type="button">Done</button></div>
    </section>
  </div>;
}
